/**
 * hero.tsx — a student page's framed hero: its picture behind, a badge, its
 * title, and what the page says under it — a campaign's role and stats, a
 * chapter's description and progress, an activity's chips. The pages
 * drew the same framed photo box from three recipes (.cdHero, .chHero,
 * .actHero); one frame now, including the portfolio’s horizontal identity, in a size per page.
 */
import type { ReactNode } from 'react';
import { Photo } from '../../../components/photo';
import styles from './hero.module.css';

/** Which page's hero: its height, its padding and its title's type. */
type HeroSize = 'campaign' | 'chapter' | 'activity' | 'portfolio';

/** Each size's class, written whole. */
const SIZE: Record<HeroSize, string> = {
  campaign: styles.campaign,
  chapter: styles.chapter,
  activity: styles.activity,
  portfolio: styles.portfolio,
};

/** A page's framed hero: the picture, the badge over the title, and the page's own lines under it. */
export function Hero({ size, image, badge, title, leading, children }: {
  size: HeroSize;
  image?: string | null;
  /** What stands over the title: a status, or an activity's kind. */
  badge: ReactNode;
  title: ReactNode;
  /** A portrait beside the portfolio’s identity and stats. */
  leading?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className={`${styles.hero} ${SIZE[size]} framed`}>
      <Photo fill src={image} />
      <div className={styles.in}>
        {leading}
        <div className={styles.body}>
          {badge}
          <h1 className={styles.title}>{title}</h1>
          {children}
        </div>
      </div>
    </header>
  );
}
